import React, { useState, useEffect } from 'react';
import { addNotification, loadNotification, updateNotification } from '../../../api/notifications';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTES } from '../../../config/routes';
import ReactQuillNew from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Updated helper function with more precise date formatting
const formatDateTime = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const CreateNotification = () => {
  const [notificationData, setNotificationData] = useState({
    title: '',
    content: '',
    beginDate: formatDateTime(new Date()) || '', // Initialize with current date
    endDate: formatDateTime(new Date()) || '',   // Initialize with current date
    image: null,
  });
  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setError] = useState('');
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const nid = queryParams.get('nid');
    if (nid) {
      setIsUpdate(true);
      const fetchNotification = async () => {
        try {
          setLoading(true);
          const notification = await loadNotification(nid);
          setNotificationData({
            id: notification.id,
            title: notification.title || '',
            content: notification.content || '',
            beginDate: formatDateTime(notification.beginDate) || '',
            endDate: formatDateTime(notification.endDate) || '',
            image: notification.image || null,
          });
          setLoading(false);
        } catch (err) {
          setError('Failed to fetch notification data');
          setLoading(false);
        }
      };
      fetchNotification();
    }
  }, [location.search]);

  const updateMessage = (content) => {
    setNotificationData(prev => ({ ...prev, content: content || '' }));
  };

  // Update handleChange to process file input for image
  const handleChange = (e) => {
    const { id, value, files } = e.target;
    if (id === 'image') {
      setNotificationData(prev => ({
        ...prev,
        image: files[0] || null
      }));
    } else {
      setNotificationData(prev => ({
        ...prev,
        [id]: value || ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    if (!notificationData.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!notificationData.content?.trim()) {
      toast.error('Message is required');
      return;
    }
    if (!notificationData.beginDate) {
      toast.error('Begin date is required');
      return;
    }
    if (!notificationData.endDate) {
      toast.error('End date is required');
      return;
    }

    setLoading(true);
    try {
      if (isUpdate) {
        await updateNotification(notificationData);
        toast.success('Notification updated successfully');
      } else {
        await addNotification(notificationData);
        toast.success('Notification created successfully');
      }
      setTimeout(() => history.push(ROUTES.ALL_NOTIFICATIONS.path), 3000);
    } catch (err) {
      setError('Operation failed');
      toast.error('Error during notification operation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
      history.push(ROUTES.ALL_NOTIFICATIONS.path);
        window.scrollTo(0, 0);
  };

  return (
    <div className="notification-wrapper overflow-hidden">
      <ToastContainer />
      <div className="container pt-120 position-relative">
        <div className="row">
          <div className="col-lg-8">
            <div className="create-notification-container">
              <h2 className="mb-3">{isUpdate ? 'Update Notification' : 'Create Notification'}</h2>
              <form className="create-notification-form" onSubmit={handleSubmit}>
                <label>Title: <span className="text-danger">*</span></label>
                <input
                  type="text"
                  id="title"
                  className="form-control mb-3"
                  value={notificationData.title}
                  onChange={handleChange}
                  required
                />
                <label>Message: <span className="text-danger">*</span></label>
                <ReactQuillNew
                    value={notificationData.content}
                    onChange={updateMessage}
                    style={{ height: '300px', margin:'0 0 60px 0' }}
                    required
                />
                <label>Begin Date: <span className="text-danger">*</span></label>
                <input
                  type="datetime-local"
                  id="beginDate"
                  className="form-control mb-3"
                  value={notificationData.beginDate}
                  onChange={handleChange}
                  required
                />
                <label>End Date: <span className="text-danger">*</span></label>
                <input
                  type="datetime-local"
                  id="endDate"
                  className="form-control mb-3"
                  value={notificationData.endDate}
                  onChange={handleChange}
                  required
                />
                <label>Image:</label>
                {notificationData.image && (
                  <div className="mb-3">
                    <img
                      src={typeof notificationData.image === 'string'
                        ? notificationData.image
                        : URL.createObjectURL(notificationData.image)}
                      alt="Notification"
                      className="img-fluid mb-3"
                      style={{ maxHeight: '200px' }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  id="image"
                  className="form-control mb-3"
                  accept="image/*"
                  onChange={handleChange}
                />
                <div className="submit-btn">
                  <button type="submit" disabled={loading} className="btn btn-primary">
                    {loading ? 'Processing...' : isUpdate ? 'Update Notification' : 'Create Notification'}
                  </button>
                </div>
                <br />
                <div className="submit-btn">
                  <button type="button" onClick={handleBack} className="btn btn-secondary">
                    <i className="bi bi-arrow-left" title="Go Back"></i>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateNotification;
